import React, { useEffect, useMemo, useState } from 'react';

import { CREATE_STUDY_ROLES, EDIT_STUDY_ROLES, hasRoles, mergeRoles } from '../../../../util/RoleUtil';

import { ToolTip } from '../../../common/tool-tip/ToolTip';
import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { StudyTrialVisitPacksTab } from '../visit-packs/StudyTrialVisitPacksTab';
import { CancelConfirmationModal } from '../../../common/cancel-confirmation-modal/CancelConfirmationModal';
import { RemoveEnrolmentScheduleModal } from './RemoveEnrolmentScheduleModal';
import { ParticipantsLinkedScheduleModal } from './ParticipantsLinkedScheduleModal';

import './StudyScheduleTab.css';

const roles = mergeRoles(CREATE_STUDY_ROLES, EDIT_STUDY_ROLES);

const defaultScheduleState = () => ({
  scheduleName: '',
  numberOfVisits: '',
  phase: 'SETUP',
  visits: [],
  studyScheduleExternalReference: undefined,
  isMigratedEmptySchedule: false,
  hasParticipantsLinked: false,
  hasVisits: false,
});

const makeId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

export const StudyScheduleTab = ({
  context,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
  setContext,
  navigateTo,
  activeTab,
  setActiveTab,
  study,
  setCurrentScreen,
  editMode,
  handleUpdate,
  setShowSitesTab,
}) => {
  const fieldsDisabled = !hasRoles(context, roles);

  const [schedules, setSchedules] = useState([
    {
      id: 'default',
      name: 'Default',
      hasParticipantsLinked: false,
      hasVisits: false,
    },
  ]);

  const [activeScheduleId, setActiveScheduleId] = useState('default');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const [isRemoveScheduleModalOpen, setIsRemoveScheduleModalOpen] = useState(false);
  const [schedulePendingRemoval, setSchedulePendingRemoval] = useState(null);

  const [isParticipantsLinkedModalOpen, setIsParticipantsLinkedModalOpen] = useState(false);
  const [participantsLinkedSchedule, setParticipantsLinkedSchedule] = useState(null);

  const [scheduleData, setScheduleData] = useState({
    default: defaultScheduleState(),
  });

  const currentSchedule = scheduleData[activeScheduleId] ?? scheduleData.default;

  const isNewSchedule = !currentSchedule?.studyScheduleExternalReference;
  const activeScheduleIndex = schedules.findIndex((schedule) => schedule.id === activeScheduleId);
  const activeScheduleHasEnrolmentVisit = (currentSchedule?.visits || []).some((visit) => !!visit.enrolmentVisit);

  const enrolmentVisitAllowedForActiveSchedule = isNewSchedule
    ? activeScheduleIndex === 0
    : activeScheduleHasEnrolmentVisit;

  const [errors, setErrors] = useState({
    scheduleName: '',
    numberOfVisits: '',
  });

  const hasAssignedForms = useMemo(() => {
    const v = Array.isArray(currentSchedule?.visits) ? currentSchedule.visits : [];
    return v.some((visit) => (visit?.formExternalReferences?.length ?? 0) > 0 || (visit?.forms?.length ?? 0) > 0);
  }, [currentSchedule?.visits]);

  const scheduleHasEnrolmentVisit = (scheduleId) => {
    const visits = scheduleData?.[scheduleId]?.visits || [];
    return visits.some((visit) => !!visit.enrolmentVisit);
  };

  const scheduleHasCapturedVisits = (scheduleId) => {
    const schedule = schedules.find((s) => s.id === scheduleId);
    const visits = scheduleData?.[scheduleId]?.visits || [];

    return (
      !!schedule?.hasVisits || !!scheduleData?.[scheduleId]?.hasVisits || visits.some((visit) => !!visit.hasVisits)
    );
  };

  const scheduleCannotBeRemoved = (schedule) => {
    if (!schedule) return false;

    return (
      !!schedule.hasParticipantsLinked ||
      !!scheduleData?.[schedule.id]?.hasParticipantsLinked ||
      scheduleHasCapturedVisits(schedule.id)
    );
  };

  const buildSchedulesFromStudy = () => {
    const visitSchedules = Array.isArray(study?.visitSchedules) ? study.visitSchedules : [];

    const allSchedulesAreMigratedEmpty =
      visitSchedules.length > 0 &&
      visitSchedules.every(
        (sched) =>
          !!sched.studyScheduleExternalReference && Array.isArray(sched.visitPacks) && sched.visitPacks.length === 0
      );

    const nextSchedules = [];
    const nextScheduleData = {};

    if (allSchedulesAreMigratedEmpty) {
      const newScheduleId = makeId();

      nextSchedules.push({
        id: newScheduleId,
        name: 'New Schedule',
        hasParticipantsLinked: false,
        hasVisits: false,
        isMigratedEmptySchedule: false,
        cannotDelete: false,
        disabled: false,
      });

      nextScheduleData[newScheduleId] = {
        ...defaultScheduleState(),
        scheduleName: '',
        numberOfVisits: '',
        visits: [],
        phase: 'SETUP',
        studyScheduleExternalReference: undefined,
        isMigratedEmptySchedule: false,
        hasParticipantsLinked: false,
        hasVisits: false,
      };
    }

    visitSchedules.forEach((sched, idx) => {
      const visitPacks = Array.isArray(sched.visitPacks) ? sched.visitPacks : [];
      const isMigratedEmptySchedule = !!sched.studyScheduleExternalReference && visitPacks.length === 0;

      const id = sched.studyScheduleExternalReference || makeId();
      const name = sched.name || `Schedule ${idx + 1}`;
      const hasParticipantsLinked = !!sched.hasParticipantsLinked;
      const hasVisits = visitPacks.some((vp) => !!vp.hasVisits);

      nextSchedules.push({
        id,
        name,
        hasParticipantsLinked,
        hasVisits,
        isMigratedEmptySchedule,
        cannotDelete: isMigratedEmptySchedule,
        disabled: isMigratedEmptySchedule,
      });

      const visits = visitPacks.map((vp) => ({
        ...vp,
        formExternalReferences: Array.isArray(vp.forms) ? vp.forms.map((f) => f.formExternalReference) : [],
        forms: Array.isArray(vp.forms) ? vp.forms : [],
        visitCode: vp.visitCode,
        hasVisits: !!vp.hasVisits,
        visitCodeToUpdate: undefined,
        visitCodeEdited: false,
      }));

      nextScheduleData[id] = {
        ...defaultScheduleState(),
        scheduleName: name,
        numberOfVisits: isMigratedEmptySchedule ? '' : String(visitPacks.length),
        phase: isMigratedEmptySchedule ? 'MIGRATED_EMPTY' : 'FORMS',
        visits,
        studyScheduleExternalReference: sched.studyScheduleExternalReference,
        isMigratedEmptySchedule,
        hasParticipantsLinked,
        hasVisits,
      };
    });

    if (nextSchedules.length === 0) {
      nextSchedules.push({
        id: 'default',
        name: 'Default',
        hasParticipantsLinked: false,
        hasVisits: false,
        isMigratedEmptySchedule: false,
        cannotDelete: false,
        disabled: false,
      });

      nextScheduleData.default = defaultScheduleState();
    }

    const firstEnabledSchedule = nextSchedules.find(
      (schedule) => !schedule.disabled && !schedule.isMigratedEmptySchedule
    );

    return {
      nextSchedules,
      nextScheduleData,
      nextActiveScheduleId: firstEnabledSchedule?.id ?? nextSchedules[0]?.id ?? 'default',
    };
  };

  useEffect(() => {
    setActiveTab('studySchedules');
    // eslint-disable-next-line
  }, [setActiveTab, activeTab]);

  useEffect(() => {
    if (!editMode) return;
    if (!study?.visitSchedules || !Array.isArray(study.visitSchedules)) return;

    const breadcrumbData = context.breadcrumbs.newStudySchedulesBreadcrumb?.data;
    const hasBreadcrumbIds = breadcrumbData?.schedules?.[0]?.id;

    setScheduleData((prev) => {
      const alreadyHydrated = Object.keys(prev || {}).some((k) => k !== 'default') || hasBreadcrumbIds;
      if (alreadyHydrated) return prev;

      const { nextSchedules, nextScheduleData, nextActiveScheduleId } = buildSchedulesFromStudy();

      setSchedules(nextSchedules);
      setActiveScheduleId(nextActiveScheduleId);

      return nextScheduleData;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, study?.visitSchedules]);

  useEffect(() => {
    if (editMode) {
      return;
    }

    const breadcrumbData = context.breadcrumbs.newStudySchedulesBreadcrumb?.data;
    if (!breadcrumbData?.schedules?.[0]?.id) return;

    const firstEnabledSchedule = breadcrumbData.schedules.find(
      (schedule) => !schedule.disabled && !schedule.isMigratedEmptySchedule
    );

    const restoredSchedule = breadcrumbData.schedules.find(
      (schedule) => schedule.id === breadcrumbData.activeScheduleId
    );

    setSchedules(breadcrumbData.schedules);
    setScheduleData(breadcrumbData.scheduleData);

    setActiveScheduleId(
      restoredSchedule && !restoredSchedule.disabled && !restoredSchedule.isMigratedEmptySchedule
        ? restoredSchedule.id
        : firstEnabledSchedule?.id ?? breadcrumbData.schedules[0]?.id ?? 'default'
    );
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (editMode) {
      return;
    }

    setContext((prev) => ({
      ...prev,
      breadcrumbs: {
        ...prev.breadcrumbs,
        newStudySchedulesBreadcrumb: {
          ...prev.breadcrumbs.newStudySchedulesBreadcrumb,
          type: 'General',
          screenKey: 'studyTrialSchedules',
          data: {
            schedules,
            scheduleData,
            activeScheduleId,
          },
        },
      },
    }));
  }, [schedules, scheduleData, activeScheduleId, setContext, editMode]);

  useEffect(() => {
    if (!editMode) return;
    if (!study?.visitSchedules || !Array.isArray(study.visitSchedules)) return;

    if (schedules.length > 1 || (schedules.length === 1 && schedules[0].id !== 'default')) return;

    const { nextSchedules, nextScheduleData, nextActiveScheduleId } = buildSchedulesFromStudy();

    if (nextSchedules.length > 0) {
      setSchedules(nextSchedules);
      setScheduleData(nextScheduleData);
      setActiveScheduleId(nextActiveScheduleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, study?.visitSchedules]);

  const handleCancelClick = () => {
    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        newStudyGeneralBreadcrumb: undefined,
        newStudyFormsBreadcrumb: undefined,
        newStudySchedulesBreadcrumb: undefined,
        newStudySitesBreadcrumb: undefined,
      },
      editMode: true,
    }));
    navigateTo('studyTrialAdmin');
  };

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  const closeRemoveScheduleModal = () => {
    setIsRemoveScheduleModalOpen(false);
    setSchedulePendingRemoval(null);
  };

  const closeParticipantsLinkedModal = () => {
    setIsParticipantsLinkedModalOpen(false);
    setParticipantsLinkedSchedule(null);
  };

  const updateScheduleField = (field, value) => {
    if (field === 'numberOfVisits' && isNaN(value)) return;

    setScheduleData((prev) => ({
      ...prev,
      [activeScheduleId]: {
        ...(prev[activeScheduleId] ?? defaultScheduleState()),
        [field]: value,
      },
    }));
  };

  const updateScheduleUI = (updates) => {
    setScheduleData((prev) => ({
      ...prev,
      [activeScheduleId]: {
        ...(prev[activeScheduleId] ?? defaultScheduleState()),
        ...updates,
      },
    }));
  };

  const handleAddScheduleClick = () => {
    const id = makeId();
    const newName = 'New Schedule';

    setSchedules((prev) => [
      ...prev,
      {
        id,
        name: newName,
        hasParticipantsLinked: false,
        hasVisits: false,
        isMigratedEmptySchedule: false,
        cannotDelete: false,
        disabled: false,
      },
    ]);

    setScheduleData((prev) => ({
      ...prev,
      [id]: {
        ...defaultScheduleState(),
        scheduleName: '',
        numberOfVisits: '',
        visits: [],
        studyScheduleExternalReference: undefined,
        isMigratedEmptySchedule: false,
        hasParticipantsLinked: false,
        hasVisits: false,
        phase: 'SETUP',
      },
    }));

    setActiveScheduleId(id);
  };

  const handleScheduleClick = (schedule) => {
    if (schedule.disabled || schedule.isMigratedEmptySchedule) {
      return;
    }

    setActiveScheduleId(schedule.id);
  };

  const performRemoveSchedule = (scheduleId) => {
    const schedule = schedules.find((s) => s.id === scheduleId);

    if (schedule?.cannotDelete || schedule?.isMigratedEmptySchedule || scheduleCannotBeRemoved(schedule)) {
      return;
    }

    setSchedules((prev) => {
      const updated = prev.filter((t) => t.id !== scheduleId);

      setActiveScheduleId((curr) => {
        if (curr !== scheduleId) return curr;
        return updated.find((schedule) => !schedule.disabled)?.id ?? updated[0]?.id ?? 'default';
      });

      return updated.length
        ? updated
        : [
            {
              id: 'default',
              name: 'Default',
              hasParticipantsLinked: false,
              hasVisits: false,
            },
          ];
    });

    setScheduleData((prev) => {
      const { [scheduleId]: _, ...rest } = prev;
      return Object.keys(rest).length ? rest : { default: defaultScheduleState() };
    });
  };

  const removeSchedule = (schedule, event) => {
    event.stopPropagation();

    if (schedule.cannotDelete || schedule.isMigratedEmptySchedule) {
      return;
    }

    if (scheduleCannotBeRemoved(schedule)) {
      setParticipantsLinkedSchedule(schedule);
      setIsParticipantsLinkedModalOpen(true);
      return;
    }

    const hasEnrolmentVisit = scheduleHasEnrolmentVisit(schedule.id);

    if (hasEnrolmentVisit) {
      setSchedulePendingRemoval(schedule);
      setIsRemoveScheduleModalOpen(true);
      return;
    }

    performRemoveSchedule(schedule.id);
  };

  const handleConfirmRemoveSchedule = () => {
    if (!schedulePendingRemoval) return;

    const replacementId = makeId();
    const replacementName = 'Default';

    setSchedules((prev) => {
      const updated = prev.filter((schedule) => schedule.id !== schedulePendingRemoval.id);

      return [
        {
          id: replacementId,
          name: replacementName,
          hasParticipantsLinked: false,
          hasVisits: false,
          isMigratedEmptySchedule: false,
          cannotDelete: false,
          disabled: false,
        },
        ...updated,
      ];
    });

    setScheduleData((prev) => {
      const { [schedulePendingRemoval.id]: _, ...rest } = prev;

      return {
        [replacementId]: {
          ...defaultScheduleState(),
          scheduleName: '',
          numberOfVisits: '',
          visits: [],
          studyScheduleExternalReference: undefined,
          isMigratedEmptySchedule: false,
          hasParticipantsLinked: false,
          hasVisits: false,
          phase: 'SETUP',
        },
        ...rest,
      };
    });

    setActiveScheduleId(replacementId);
    closeRemoveScheduleModal();
  };

  const validateScheduleInputs = () => {
    const newErrors = { scheduleName: '', numberOfVisits: '' };
    let isValid = true;

    if (!currentSchedule?.scheduleName?.trim()) {
      newErrors.scheduleName = '*REQUIRED';
      isValid = false;
    }

    const n = Number(currentSchedule?.numberOfVisits);

    if (!currentSchedule?.numberOfVisits) {
      newErrors.numberOfVisits = '*REQUIRED';
      isValid = false;
    } else if (isNaN(n) || n < 1) {
      newErrors.numberOfVisits = 'Must be 1 or more';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCreateVisitsClick = () => {
    const isValid = validateScheduleInputs();
    if (!isValid) return;

    const enteredName = currentSchedule?.scheduleName?.trim();
    const n = Number(currentSchedule?.numberOfVisits);

    if (!enteredName) {
      showToaster({
        responseStatus: 'Warning',
        responseMessage: 'Please enter a schedule name before creating visits.',
      });
      return;
    }

    setSchedules((prev) => prev.map((t) => (t.id === activeScheduleId ? { ...t, name: enteredName } : t)));

    setScheduleData((prev) => ({
      ...prev,
      [activeScheduleId]: {
        ...(prev[activeScheduleId] ?? defaultScheduleState()),
        scheduleName: enteredName,
        numberOfVisits: String(n),
        phase: 'VISITS',
      },
    }));
  };

  const setVisitsForActiveSchedule = (next) => {
    setScheduleData((prev) => {
      const current = prev[activeScheduleId] ?? defaultScheduleState();
      const prevVisits = Array.isArray(current.visits) ? current.visits : [];
      const computed = typeof next === 'function' ? next(prevVisits) : next;
      const nextVisits = Array.isArray(computed) ? computed : prevVisits;
      const nextHasVisits = nextVisits.some((visit) => !!visit.hasVisits);

      return {
        ...prev,
        [activeScheduleId]: {
          ...current,
          visits: nextVisits,
          hasVisits: nextHasVisits,
        },
      };
    });

    setSchedules((prev) =>
      prev.map((schedule) => {
        if (schedule.id !== activeScheduleId) return schedule;

        const current = scheduleData[activeScheduleId] ?? defaultScheduleState();
        const prevVisits = Array.isArray(current.visits) ? current.visits : [];
        const computed = typeof next === 'function' ? next(prevVisits) : next;
        const nextVisits = Array.isArray(computed) ? computed : prevVisits;

        return {
          ...schedule,
          hasVisits: nextVisits.some((visit) => !!visit.hasVisits),
        };
      })
    );
  };

  const setPhaseForActiveSchedule = (phase) => {
    updateScheduleUI({ phase });
  };

  const setNumberOfVisitsForActiveSchedule = (next) => {
    setScheduleData((prev) => {
      const current = prev[activeScheduleId] ?? defaultScheduleState();
      const prevCount = Number(current.numberOfVisits || 0);

      const computed = typeof next === 'function' ? next(prevCount) : next;
      const nextCount = Number(computed);

      return {
        ...prev,
        [activeScheduleId]: {
          ...current,
          numberOfVisits: String(Number.isFinite(nextCount) ? nextCount : prevCount),
        },
      };
    });
  };

  return (
    <div className="study-trials-schedules-tab-container">
      <div className="study-trials-schedules-tab-container-content">
        <div className="study-trials-schedules-tab-tab-container">
          {schedules.map((schedule) => (
            <div
              key={schedule.id}
              className={`study-trials-schedules-tab ${activeScheduleId === schedule.id ? 'active-tab' : ''} ${
                schedule.disabled || schedule.isMigratedEmptySchedule ? 'disabled-tab migrated-empty-schedule-tab' : ''
              }`}
              onClick={() => handleScheduleClick(schedule)}
            >
              {!schedule.cannotDelete && !schedule.isMigratedEmptySchedule && (
                <div className="close-icon" onClick={(event) => removeSchedule(schedule, event)}>
                  &#x2716;
                </div>
              )}

              <div className="study-trials-schedules-tab-text">{schedule.name}</div>
            </div>
          ))}

          <div
            className={`study-trials-schedules-tab ${!editMode && !hasAssignedForms ? 'disabled-tab' : ''}`}
            onClick={() => {
              if (!editMode && !hasAssignedForms) {
                showToaster({
                  responseStatus: 'Warning',
                  responseMessage: 'Please assign at least one form to a visit before adding another schedule.',
                });
                return;
              }

              handleAddScheduleClick();
            }}
          >
            Add Schedule
          </div>
        </div>

        <div className="study-trials-schedules-tab-content">
          <div className="scrollable">
            <div className="schedules-tab-container">
              <div className="study-trials-schedules-tab-inputs">
                {currentSchedule?.phase === 'SETUP' && (!editMode || isNewSchedule) && (
                  <div className="form-section-left">
                    <div className="input-container">
                      <label className="input-label-sm">Schedule Name?</label>
                      <div className="field-group">
                        <input
                          type="text"
                          className={`text-input-long ${errors.scheduleName ? 'error' : ''}`}
                          value={currentSchedule?.scheduleName || ''}
                          onChange={({ target }) => {
                            updateScheduleField('scheduleName', target.value);
                            setErrors((prev) => ({ ...prev, scheduleName: '' }));
                          }}
                        />
                        {errors.scheduleName && <p className="field-error">{errors.scheduleName}</p>}
                      </div>
                    </div>

                    <div className="input-container">
                      <label className="input-label">How many visits would you like to add?</label>
                      <div className="field-group">
                        <input
                          className={`text-input-small ${errors.numberOfVisits ? 'error' : ''}`}
                          value={currentSchedule?.numberOfVisits || ''}
                          disabled={fieldsDisabled}
                          onChange={({ target }) => {
                            updateScheduleField('numberOfVisits', target.value);
                            setErrors((prev) => ({ ...prev, numberOfVisits: '' }));
                          }}
                        />
                        {errors.numberOfVisits && <p className="field-error">{errors.numberOfVisits}</p>}
                      </div>

                      {toolTipsEnabled && (
                        <ToolTip
                          tooltipExternalReference={'amountOfVisits123'}
                          context={context}
                          setSpinnerText={setSpinnerText}
                          showToaster={showToaster}
                          fieldName={'How many visits would you like to add?'}
                        />
                      )}
                    </div>
                  </div>
                )}

                {currentSchedule?.phase !== 'SETUP' && currentSchedule?.phase !== 'MIGRATED_EMPTY' && (
                  <StudyTrialVisitPacksTab
                    context={context}
                    setCurrentScreen={setCurrentScreen}
                    setContext={setContext}
                    amountOfVisits={currentSchedule?.numberOfVisits}
                    setNumberOfVisits={setNumberOfVisitsForActiveSchedule}
                    setSpinnerText={setSpinnerText}
                    showToaster={showToaster}
                    editMode={editMode}
                    navigateTo={navigateTo}
                    visits={currentSchedule?.visits || []}
                    setVisits={setVisitsForActiveSchedule}
                    study={study}
                    phase={currentSchedule?.phase}
                    setPhase={setPhaseForActiveSchedule}
                    schedules={schedules}
                    scheduleData={scheduleData}
                    handleUpdate={handleUpdate}
                    scheduleExternalReference={currentSchedule?.studyScheduleExternalReference}
                    activeScheduleId={activeScheduleId}
                    isNewSchedule={isNewSchedule}
                    scheduleName={currentSchedule?.scheduleName}
                    setShowSitesTab={setShowSitesTab}
                    enrolmentVisitAllowed={enrolmentVisitAllowedForActiveSchedule}
                    scheduleHasParticipantsLinked={!!currentSchedule?.hasParticipantsLinked}
                  />
                )}
              </div>
            </div>
          </div>

          {isCancelModalOpen && (
            <CancelConfirmationModal
              closeCancelModal={closeCancelModal}
              handleCancelClick={handleCancelClick}
              setIsCancelModalOpen={setIsCancelModalOpen}
            />
          )}

          {isParticipantsLinkedModalOpen && (
            <ParticipantsLinkedScheduleModal
              closeModal={closeParticipantsLinkedModal}
              scheduleName={participantsLinkedSchedule?.name}
            />
          )}

          {isRemoveScheduleModalOpen && (
            <RemoveEnrolmentScheduleModal
              closeModal={closeRemoveScheduleModal}
              handleConfirmRemoveSchedule={handleConfirmRemoveSchedule}
            />
          )}

          {currentSchedule?.phase === 'SETUP' && (!editMode || isNewSchedule) && (
            <div className="study-trials-schedules-sticky-footer">
              <SaveButton
                color="#ffffff"
                border="1px solid #deab26"
                textColor="#deab26"
                title="Cancel"
                onClick={() => setIsCancelModalOpen(true)}
              />
              <SaveButton color="#deab26" title="Create Visits" onClick={handleCreateVisitsClick} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
